import RoutesUtils from '../../js/RoutesUtils.js';

/**
 * El permiso del módulo de empleados, en un solo sitio.
 *
 * [revisión #1711] Estaba repartido en cuatro: la ruta lo declaraba con su lista de roles, el Mapa
 * de aprobación la copiaba para decidir si el nombre va enlazado, la ficha la copiaba otra vez para
 * decidir si se pinta entera, y los botones «Editar» y «Eliminar» miraban el rol a secas, sin pasar
 * por `tieneAcceso`. Esa cuarta copia respondía distinto que las otras tres: con el módulo dado por
 * el menú a otro rol, `/empleados/edit/:id/` dejaba entrar pero la ficha de consulta no ofrecía
 * «Editar»; y al revés, un ADMIN cuyo menú no trae empleados conservaba los dos botones sobre una
 * ficha que ya se había quedado en tarjeta de vacaciones, así que «Editar» sacaba un «Guardar» que
 * llamaba a un formulario desmontado.
 *
 * Con una pregunta por permiso y nadie resolviéndola por su cuenta, el quinto sitio que lo necesite
 * no puede volver a contestar otra cosa.
 */

// Quién tiene el módulo de empleados mientras el menú todavía no ha llegado. Con el menú cargado
// manda el menú, que es el control de acceso real de las rutas (ver `RoutesUtils.tieneAcceso`).
const ROLES_MODULO = ['ADMIN', 'INSPECTOR'];

// DIRECTOR entra en la ficha de **lectura** aunque el módulo de empleados no esté en su menú:
// llega enlazado desde el Mapa de aprobación y lo que va a ver ahí es la tarjeta de vacaciones.
// Eso no le da la ficha entera (`verFichaCompleta`) ni la edición (`gestionar`).
const ROLES_FICHA_LECTURA = ROLES_MODULO.concat(['DIRECTOR']);
const ROLES_DIRECTOS_FICHA_LECTURA = ['DIRECTOR'];

const PermisosEmpleados = {
    ROLES_MODULO: ROLES_MODULO,
    ROLES_FICHA_LECTURA: ROLES_FICHA_LECTURA,
    ROLES_DIRECTOS_FICHA_LECTURA: ROLES_DIRECTOS_FICHA_LECTURA,

    /**
     * Si se puede entrar en la ficha de lectura. Es la pregunta de quien enlaza a ella: con los
     * mismos argumentos que la ruta, o el enlace y la pantalla dirían cosas distintas.
     */
    abrirFicha: function() {
        return RoutesUtils.tieneAcceso('/empleados/view/', ROLES_FICHA_LECTURA,
            ROLES_DIRECTOS_FICHA_LECTURA);
    },

    /**
     * Si se tiene el módulo de empleados, que es lo que da derecho a ver una ficha de personal
     * entera: correo, teléfono, DNI, firma, altas y bajas y los datos de organización.
     *
     * [JJCS, 01/10/2026] Sin los roles directos a propósito: tener acceso a una pantalla no es
     * tener derecho a los datos personales de un compañero. Quién puede ver los datos de vacaciones
     * de otra persona lo decide la API en cada petición, no esta lista.
     */
    verFichaCompleta: function() {
        return RoutesUtils.tieneAcceso('/empleados/view/', ROLES_MODULO);
    },

    /** Si se puede editar o eliminar el empleado: el mismo criterio que `/empleados/edit/:id/`. */
    gestionar: function() {
        return RoutesUtils.tieneAcceso('/empleados/edit/', ROLES_MODULO);
    },

    /**
     * [#932, Dirección 26/10/2026] Si se puede asignar o cambiar el rol, dar o quitar el usuario de
     * aplicación y eliminar la ficha de quien lo tiene: solo ADMIN, tenga quien tenga el módulo.
     *
     * Va por el rol y no por el menú porque es lo que comprueba la API
     * (`PermisosFichaEmpleado`/`AuthzService.canCambiarAccesoEmpleado`). Esto solo decide qué se
     * ofrece: el rechazo de verdad lo hace el servidor en cada guardado.
     */
    cambiarAcceso: function() {
        return RoutesUtils.checkRolesPermission(['ADMIN']);
    }
};

export default PermisosEmpleados;
